import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useOfficeMailboxes } from '../../utils/officeMailboxes';   // the office calendars (every firm's office mailbox)
import { useStore } from '../../store';
import { fmtDateTime } from '../../utils/region';
import { noRuleFor, cleanNo, fitsNo, noBoxW, retypeNo } from '../../utils/docNumber';   // a document's own number (report c8ad7a51)
import Pagination, { getPageSize } from '../Pagination';

// Document Storage upload wizard (owner/Calem's flow, 2026-08-15; reworked to the 2026-08-25 list):
//   1/2. Document type and Company are DROPDOWNS fed from the server (no free text) — types are the
//        names Our Documents itself uses and the entries admins add (TypePicker's "+ Add", report
//        b7482e45); companies from the client master, by their
//        bare name ("Samui Host", not "Samui Host Co., Ltd."), which is also what the file is named with.
//   3.   The Company list opens with "Please select…" then "No company (personal document)".
//   4.   Dates are DISPLAYED and typed as DD/MM/YYYY (stored internally as YYYY-MM-DD); the AI's
//        double-check messages use DD/MM/YYYY too.
//   5.   The folder list pins the AI-suggested destination on top, highlighted; every other folder
//        stays visible but greyed (still selectable for deliberate overrides).
//   6/8. After a successful save the wizard auto-opens the archive folder in a new tab, and — for
//        expiry-dated documents — a second tab with the Calendar positioned on the expiry date.
//        Popup-blocked tabs fall back to buttons on the success screen.
//   7.   Multi-upload may MIX types (owner/Calem 2026-08-26): the document type AND the destination
//        folder live on each file card, prefilled from that file's own AI read. For homogeneous
//        batches "Apply the same:" sits under the ④ label, above the file cards and outside them
//        (Calem, report ebd7e8e6), with four bubbles — Company / Date / Type / Folder — each copying
//        only that field of file 1 over the rest, so any one or more can be used.

type Folder = { id: string; parent_id: string; name: string };
type Analysis = { docType: string; personName: string; companyName: string; docNumber: string; expiryDate: string; issueDate: string; dob: string; confidence: string; notes: string; suggestedFolderId: string };
type Entry = {
  uid: string;
  file: File; ai: Analysis | null; aiErr: string;
  origMb?: string;                            // set when the file was auto-compressed (original size)
  person: string; company: string;            // company: '' = not selected, '__none__' = explicitly none
  companyHint: string; typeHint: string;
  companyGuessed?: boolean;                   // the company was guessed from the person's jobs / files (report b7482e45)
  companyByHand?: boolean;                    // the operator picked the company ("No company" too) — never guessed over
  dateTxt: string;                            // DD/MM/YYYY — filled from the AI's read, or typed / picked by hand
  dateConfirmed: boolean;                     // false while an AI-filled date waits for the operator's tick
  // Per-file type + destination (owner/Calem 2026-08-26): a batch may MIX types (ID Cards + a
  // passport), so the type and the folder live on each file, prefilled from that file's own AI read.
  type: string; sugId: string; folderId: string;
  docNo?: string;                             // the document's own number (report c8ad7a51) — NO_RULES per type, never required
  folderOpen?: boolean; folderQ?: string;     // UI state of the per-card folder picker
};

const NONE = '__none__';
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', borderRadius: 8, color: 'var(--text-primary)', boxSizing: 'border-box' };

// Searchable company combobox (owner 2026-08-25) — 130+ clients made the native <select> useless.
// Focus opens the list and clears for typing; blur without a pick falls back to the current value.
function CompanyPicker({ value, companies, onPick }: { value: string; companies: string[]; onPick: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const shown = q.trim() ? companies.filter(c => c.toLowerCase().includes(q.trim().toLowerCase())) : companies;
  const label = value === NONE ? '— No company (personal document) —' : value;
  const row: React.CSSProperties = { padding: '7px 10px', fontSize: 12.5, cursor: 'pointer', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  return (
    <div style={{ position: 'relative' }}>
      <input value={open ? q : label} placeholder={open ? 'Type to search…' : 'Please select…'}
        onFocus={() => { setOpen(true); setQ(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={ev => setQ(ev.target.value)}
        style={{ ...inp, paddingRight: 26 }} />
      <i className="ti ti-selector" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'var(--text-tertiary)', pointerEvents: 'none' }} aria-hidden />
      {open && (
        <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 2, zIndex: 80, maxHeight: 240, overflowY: 'auto', background: 'var(--bg-surface)', border: '1px solid var(--border-strong)', borderRadius: 8, boxShadow: '0 8px 22px rgba(0,0,0,0.18)' }}>
          <div onMouseDown={() => { onPick(NONE); setOpen(false); }} style={{ ...row, color: 'var(--text-secondary)' }}
            onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
            — No company (personal document) —
          </div>
          {shown.map(c => (
            <div key={c} onMouseDown={() => { onPick(c); setOpen(false); }} style={row}
              onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--bg-active)')} onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}>
              {c}
            </div>
          ))}
          {shown.length === 0 && <div style={{ padding: '7px 10px', fontSize: 12, color: 'var(--text-tertiary)' }}>No match</div>}
        </div>
      )}
    </div>
  );
}

// ── Document type picker for admins (report b7482e45) ──
// The same list as everyone's drop-down, in the look of Petty Cash's To picker: a fixed overlay, so the
// wizard's scroll can't clip it (it opens upwards when the room below is short), with a search box.
// While the mouse is on a type, a pencil (Rename) and an × (Remove) show at its right end, as on that
// To list. Rename opens a box in the row — the ✓ or Enter saves, Escape or the × cancels; Remove asks
// first. Either is saved at once for everyone (PUT /doc-storage/types) and changes only this list: no
// file in Our Documents is renamed. onEdited(from, to) then reloads it; to = '' after a Remove.
// Below the list, as that picker's "Other" sits below its own (3rd opening), "+ Add “<search words>”"
// saves the search words as a new entry for everyone and picks it on this card: onEdited('', name).
// It stays greyed while the search box is empty or holds a type the list already has.
const DS_TYPE_MAX = 120;   // the server's TYPE_NAME_MAX
// The server's typeKey (routes/docStorage.ts): case, spaces and punctuation don't make another type.
const dsTypeKey = (s: string): string => { const l = s.toLowerCase(); return l.replace(/[^\p{L}\p{M}\p{N}]+/gu, '') || l.trim(); };
function TypePicker({ value, types, onPick, onEdited }: { value: string; types: string[]; onPick: (v: string) => void; onEdited: (from: string, to: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<{ left: number; top: number; bottom: number; width: number } | null>(null);
  const [hover, setHover] = useState('');         // the type under the mouse
  const [renaming, setRenaming] = useState('');   // the type whose Rename box is open
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editErr, setEditErr] = useState('');
  useEffect(() => {
    if (!open) return;
    const recalc = () => { const el = triggerRef.current; if (!el) return; const r = el.getBoundingClientRect(); setRect({ left: r.left, top: r.top, bottom: r.bottom, width: r.width }); };
    recalc();
    window.addEventListener('scroll', recalc, true); window.addEventListener('resize', recalc);
    return () => { window.removeEventListener('scroll', recalc, true); window.removeEventListener('resize', recalc); };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (ev: MouseEvent) => {
      const tgt = ev.target as Node;
      if (triggerRef.current?.contains(tgt) || boxRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const ql = q.trim().toLowerCase();
  const shown = ql ? types.filter(n => n.toLowerCase().includes(ql)) : types;
  const pick = (v: string) => { onPick(v); setOpen(false); };
  const edit = async (n: string, action: 'rename' | 'remove') => {
    if (busy) return;
    const to = newName.trim();
    if (action === 'rename' && (!to || to === n)) { setRenaming(''); return; }
    if (action === 'remove' && !window.confirm(t('ds_type_remove_confirm', { name: n }))) return;
    setBusy(true); setEditErr('');
    try {
      const r = await api.docStorageTypeEdit({ name: n, action, ...(action === 'rename' ? { to } : {}) });
      setRenaming('');
      onEdited(n, action === 'rename' ? r?.name || to : '');
    } catch (e: any) { setEditErr(e?.message || ''); } finally { setBusy(false); }
  };
  // + Add: the search words, as the list will show them (the server tidies them as for a Rename). Words the
  // server would refuse — listed already, no letter, too long — are never sent: a refusal alerts the admins.
  const typed = q.trim();
  const typedListed = !!typed && types.some(n => dsTypeKey(n) === dsTypeKey(typed));
  const typedOk = /\p{L}/u.test(typed) && typed.length <= DS_TYPE_MAX;
  const canAdd = !!typed && !typedListed && typedOk && !busy;
  const add = async () => {
    if (!canAdd) return;
    setBusy(true); setEditErr('');
    try {
      const r = await api.docStorageTypeEdit({ name: typed, action: 'add' });
      const n = r?.name || typed;
      onEdited('', n);
      pick(n);
    } catch (e: any) { setEditErr(e?.message || ''); } finally { setBusy(false); }
  };
  const toolBtn: React.CSSProperties = { width: 22, height: 22, padding: 0, borderRadius: 'var(--radius-sm)', border: 'none', background: 'var(--bg-surface)', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-sm)' };
  // A type's row: the name (a click picks it) with the two tools over its right end on hover, or, while
  // it is being renamed, the box with ✓ / ×.
  const row = (n: string) => {
    if (renaming === n) return (
      <div key={n} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 6px 4px 10px', background: 'var(--bg-active)' }}>
        <input autoFocus value={newName} maxLength={DS_TYPE_MAX} disabled={busy} aria-label={t('ds_type_rename')}
          onChange={ev => setNewName(ev.target.value)}
          onKeyDown={ev => { if (ev.key === 'Enter') void edit(n, 'rename'); else if (ev.key === 'Escape') setRenaming(''); }}
          style={{ ...inp, width: 'auto', flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 12.5 }} />
        <button type="button" title={t('save')} aria-label={t('save')} disabled={busy} onClick={() => void edit(n, 'rename')} style={toolBtn}>
          <i className="ti ti-check" style={{ fontSize: 12 }} aria-hidden />
        </button>
        <button type="button" title={t('cancel')} aria-label={t('cancel')} disabled={busy} onClick={() => setRenaming('')} style={toolBtn}>
          <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden />
        </button>
      </div>
    );
    const on = n === value;
    return (
      <div key={n} style={{ position: 'relative' }} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(h => (h === n ? '' : h))}>
        <div title={n} onClick={() => pick(n)}
          style={{ padding: '8px 10px', fontSize: 12.5, cursor: 'pointer', color: 'var(--text-primary)', fontWeight: on ? 700 : 500, background: on || hover === n ? 'var(--bg-active)' : 'transparent', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {n}
        </div>
        {hover === n && !busy && (
          <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 4 }}>
            <button type="button" title={t('ds_type_rename')} aria-label={t('ds_type_rename')} onClick={() => { setRenaming(n); setNewName(n); setEditErr(''); }} style={toolBtn}>
              <i className="ti ti-pencil" style={{ fontSize: 12 }} aria-hidden />
            </button>
            <button type="button" title={t('ds_type_remove')} aria-label={t('ds_type_remove')} onClick={() => void edit(n, 'remove')} style={toolBtn}>
              <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden />
            </button>
          </span>
        )}
      </div>
    );
  };
  const below = rect ? window.innerHeight - rect.bottom - 8 : 0;
  const above = rect ? rect.top - 8 : 0;
  const up = below < 240 && above > below;

  return (
    <>
      <button type="button" ref={triggerRef} onClick={() => { setQ(''); setRenaming(''); setEditErr(''); setOpen(o => !o); }}
        style={{ ...inp, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ color: value ? 'var(--text-primary)' : 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || t('ds_type_pick')}</span>
        <i className="ti ti-chevron-down" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
      </button>
      {open && rect && (
        <div ref={boxRef} style={{ position: 'fixed', left: rect.left, width: rect.width, ...(up ? { bottom: window.innerHeight - rect.top + 2 } : { top: rect.bottom + 2 }), maxHeight: Math.min(320, up ? above : below), zIndex: 1001, background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
            <input autoFocus value={q} onChange={ev => setQ(ev.target.value)} placeholder={t('ds_type_search')}
              onKeyDown={ev => { if (ev.key === 'Escape') setOpen(false); }} style={inp} />
            {editErr && <div style={{ fontSize: 11.5, color: '#E24B4A', marginTop: 6 }}>{editErr}</div>}
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {shown.map(row)}
            {!shown.length && <div style={{ padding: 10, fontSize: 12, color: 'var(--text-tertiary)' }}>{t('ds_type_none')}</div>}
          </div>
          <button type="button" onClick={() => void add()} disabled={!canAdd}
            title={!typed ? t('ds_type_add_need') : typedListed ? t('ds_type_add_listed') : !typedOk ? t('ds_type_add_bad', { n: DS_TYPE_MAX }) : t('ds_type_add_tip')}
            style={{ border: 'none', borderTop: '1px solid var(--border)', padding: 10, background: 'transparent', color: canAdd ? 'var(--accent)' : 'var(--text-tertiary)', fontWeight: 700, fontSize: 12.5, cursor: canAdd ? 'pointer' : 'default', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <i className="ti ti-plus" style={{ marginRight: 6 }} aria-hidden />{t('ds_type_add')}{typed ? ` “${typed}”` : ''}
          </button>
        </div>
      )}
    </>
  );
}
const lbl: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)', display: 'block', margin: '10px 0 4px' };
const todayStr = () => new Date().toISOString().slice(0, 10);
const clean = (s: string) => s.replace(/[/\\:*?"<>|()]/g, ' ').replace(/\s+/g, ' ').trim();
// A real calendar day in YYYY-MM-DD — "2033-02-31" has the shape but isn't one.
const isRealIso = (iso: string): boolean => {
  const d = new Date(`${iso}T00:00:00Z`);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) && !isNaN(d.getTime()) && d.toISOString().slice(0, 10) === iso;
};
// DD/MM/YYYY ↔ YYYY-MM-DD (display vs storage, point 4). The box is typed into (report b7c020ac), so
// "-" or "." pass for "/" and a lone digit for day or month ("3.9.2033"); anything that isn't a real
// calendar day — half-typed, or "31/02/2033" — reads as '' (no date).
export const dmyToIso = (txt: string): string => {
  const m = txt.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  const iso = m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}` : '';
  return isRealIso(iso) ? iso : '';
};
// dd-MMM-yyyy with the ENGLISH month (owner 2026-08-25: "18-NOV-2026", not "18-11-2026").
const MON_EN = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const dmyEn = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}-${MON_EN[Number(m[2]) - 1] || m[2]}-${m[1]}` : iso;
};
export const isoToDmy = (iso: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : iso);
// The date the AI read for this kind (expiry / issue) as the box's DD/MM/YYYY; '' when it read none,
// or something that isn't a real calendar date — the box then stays empty for the operator.
const aiDateFor = (a: Analysis | null, kind: string): string => {
  const iso = (kind === 'expiry' ? a?.expiryDate : kind === 'issue' ? a?.issueDate : '') || '';
  return isRealIso(iso) ? isoToDmy(iso) : '';
};

// Day-first date box (Calem, report b7c020ac: "Change the date format from month, day, year in this box
// to day, month, year"). A native <input type="date"> is drawn in the browser's own locale order —
// 08/30/2033 on a US-English browser — and the page can't reorder it, so the box is plain text in
// DD/MM/YYYY and the calendar button opens a hidden native date input with showPicker(). Both hand
// back DD/MM/YYYY; a typed "3.9.2033" is tidied to 03/09/2033 when the box loses focus.
// Exported, with dmyToIso / isoToDmy, for Drop File's AI pop-up (DropProposals), and for Tracking's
// Company Documents card (report f8a05a69), which hands in its own placeholder and its card's box (style).
export function DayFirstDateInput({ value, onChange, placeholder, style }: { value: string; onChange: (dmy: string) => void; placeholder?: string; style?: React.CSSProperties }) {
  const { t } = useTranslation();
  const pickerRef = useRef<HTMLInputElement>(null);
  const iso = dmyToIso(value);
  const box: React.CSSProperties = { ...inp, ...style };
  return (
    <div style={{ position: 'relative' }}>
      <input value={value} onChange={ev => onChange(ev.target.value)} placeholder={placeholder || t('ds_wiz_date_ph')} autoComplete="off"
        onBlur={() => { if (iso && isoToDmy(iso) !== value) onChange(isoToDmy(iso)); }}
        style={{ ...box, paddingRight: 30 }} />
      {/* Invisible but laid out over the box — with display:none the browser has nothing to hang its
          calendar under — so the calendar opens just below the box, where the native one did. */}
      <input ref={pickerRef} type="date" value={iso} onChange={ev => onChange(isoToDmy(ev.target.value))}
        tabIndex={-1} aria-hidden style={{ ...box, position: 'absolute', left: 0, top: 0, opacity: 0, pointerEvents: 'none' }} />
      <button onClick={() => { try { pickerRef.current?.showPicker(); } catch { /* no showPicker (older browser) — typing still works */ } }}
        title={t('ds_wiz_date_pick')} aria-label={t('ds_wiz_date_pick')}
        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', padding: 2, border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <i className="ti ti-calendar" style={{ fontSize: 15 }} aria-hidden />
      </button>
    </div>
  );
}


// ── Oversize auto-compression (owner 2026-08-26; 5 MB for images AND PDFs — Calem, report 280d8c4d) ──
// Instead of bouncing big files back ("please compress it"), the wizard makes anything over 5 MB
// smaller by itself. IMAGES are re-encoded in the browser: EXIF orientation honoured, long edge capped
// at 3000 px, JPEG q0.85 stepping down until it fits — a passport photo stays perfectly legible.
// PDFs have no reliable in-browser compressor, so the server re-draws their pages (shrinkPdf below).
// Whatever can't be made smaller is kept as it is, up to the 20 MB cap.
const COMPRESS_OVER = 5 * 1024 * 1024;    // images and PDFs above this get compressed
const INTAKE_MAX = 20 * 1024 * 1024;      // hard cap for anything (image or PDF)
const mbOf = (n: number) => (n / 1024 / 1024).toFixed(1);
async function compressImage(f: File): Promise<File> {
  let bmp: ImageBitmap;
  try { bmp = await createImageBitmap(f, { imageOrientation: 'from-image' } as any); }
  catch { bmp = await createImageBitmap(f); }
  const LONG = 3000;
  const scale = Math.min(1, LONG / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale)), h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(bmp, 0, 0, w, h);
  (bmp as any).close?.();
  const jpgName = f.name.replace(/\.[^.]+$/, '') + '.jpg';
  for (const q of [0.85, 0.7, 0.55, 0.4]) {
    const blob: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/jpeg', q));
    if (blob && blob.size <= COMPRESS_OVER) return new File([blob], jpgName, { type: 'image/jpeg' });
  }
  throw new Error('still too large after compression');
}
/** The server's smaller copy of an oversize PDF, or null when nothing smaller came back (or the
 *  server couldn't do it) — the caller then keeps the original. */
async function shrinkPdf(f: File): Promise<File | null> {
  try {
    const blob = await api.docStorageShrinkPdf(f);
    return blob && blob.size < f.size ? new File([blob], f.name, { type: 'application/pdf' }) : null;
  } catch { return null; }
}

/** Case-insensitive match of an AI suggestion against the controlled list. */
const matchToList = (raw: string, list: string[]): string => {
  const t = raw.trim().toLowerCase();
  if (!t) return '';
  return list.find(x => x.toLowerCase() === t)
    || list.find(x => x.toLowerCase().includes(t) || t.includes(x.toLowerCase()))
    || '';
};

// ── A document's own number (Calem, report c8ad7a51) ──
// The rules — which types carry a number, and the characters and length of each — are in utils/docNumber.ts
// (NO_RULES), shared with the chat's document-upload group since report 7dc8999f.
// The AI writes what it read in its note ("Chanote No. 53658"), so a number of up to 5
// digits there fills the box in advance — the operator can change it or empty it. A date is never a
// deed number, and a bare 1900-2099 is read as a year unless the note labels it as the number.
const deedNoFromNote = (notes: string): string => {
  const txt = String(notes || '').replace(/\d{1,4}\s*[./-]\s*\d{1,2}\s*[./-]\s*\d{1,4}/g, ' ');
  const labelled = /(?:no|number|เลขที่)\s*\.?\s*:?\s*(\d{1,5})(?!\d)/i.exec(txt);
  if (labelled) return labelled[1];
  const bare = (txt.match(/\b\d{1,5}\b/g) || []).filter(n => !(n.length === 4 && Number(n) >= 1900 && Number(n) <= 2099));
  return bare[0] || '';
};
/** The number to open the box with: the one the AI read off the document (/analyze's docNumber), kept
 *  only when it fits this type's rule, and for a title deed the number in the AI's note if it doesn't. */
const aiNoFor = (type: string, a: Analysis | null): string => {
  const r = noRuleFor(type);
  if (!r) return '';
  const read = cleanNo(String(a?.docNumber || ''), r);
  if (fitsNo(read, r)) return read;
  return r.fromNote ? cleanNo(deedNoFromNote(a?.notes || ''), r) : '';
};

// ── Simulation check results (report 88c27aca) ──
// The tests confirmed so far, newest first: when and by whom, which file (and the name it would have
// been saved as), the verdict, and each changed field as the AI's value → the corrected one. Admins
// only, like the tick box that makes them; the records are server/src/routes/docStorage.ts's ds_sim_checks.
// Exported for the chat's document-upload group (Chat/ChatPanel.tsx, report 972661d6), whose tests go
// into the same list: one date box for whichever date the document gave, its dateKind the one the read
// named ('none', named as that box is, for a date off the file's name or today's).
// Report 972661d6, 4th opening ("I want AI to learn from these results"): a line over the list says how
// many of the tests the AI is learning from (the server's `lessons`), and each of them is tagged
// Learning (`teaching`), so a tester can see which results are used.
type SimSide = { type: string; person: string; company: string; date: string; folderId: string; folder: string; name: string };
type SimRec = { id: string; at: number; by_name: string; file: string; dateKind: string; ai: SimSide; final: SimSide; changed: string[]; verdict: string; teaching?: boolean };
const LEARN_FG = '#1D4ED8';
export function SimResults({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize);
  const [data, setData] = useState<{ rows: SimRec[]; total: number; lessons: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);   // null = no error; '' = failed without a message
  useEffect(() => {
    let live = true;
    setErr(null);
    api.docStorageSimChecks(page, limit)
      .then((r: any) => { if (live) setData({ rows: r.rows || [], total: Number(r.total) || 0, lessons: Number(r.lessons) || 0 }); })
      .catch((e: any) => { if (live) setErr(e?.message || ''); });
    return () => { live = false; };
  }, [page, limit]);
  const th: React.CSSProperties = { textAlign: 'left', fontSize: 13, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 12px', lineHeight: '16px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-strong)', position: 'sticky', top: 0, zIndex: 1, background: 'var(--bg-surface)' };
  const td: React.CSSProperties = { padding: '8px 12px', fontSize: 12.5, verticalAlign: 'top', borderBottom: '1px solid var(--border)' };
  const fieldKey: Record<string, string> = { type: 'ds_sim_f_type', person: 'ds_sim_f_person', company: 'ds_sim_f_company', folder: 'ds_sim_f_folder' };
  const dateKey = (r: SimRec): string => (r.dateKind === 'issue' ? 'ds_sim_f_issue' : r.dateKind === 'expiry' ? 'ds_sim_f_expiry' : 'chat_doc_date');
  const fieldName = (f: string, r: SimRec): string => t(f === 'date' ? dateKey(r) : fieldKey[f] || f);
  const valueOf = (f: string, side: SimSide | undefined): string => {
    if (!side) return '—';
    const v = f === 'date' ? isoToDmy(side.date) : f === 'folder' ? side.folder : (side as any)[f];
    return v || '—';
  };
  const note: React.CSSProperties = { padding: 20, fontSize: 12.5, color: 'var(--text-tertiary)' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 900, maxWidth: '94vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '16px 20px 10px', fontSize: 15, fontWeight: 800 }}>
          <i className="ti ti-checklist" aria-hidden />{t('ds_sim_results_title')}
          <span style={{ flex: 1 }} />
          <button onClick={onClose} title={t('ds_sim_close')} aria-label={t('ds_sim_close')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-tertiary)', display: 'inline-flex' }}>
            <i className="ti ti-x" style={{ fontSize: 18 }} />
          </button>
        </div>
        {err === null && !!data?.total && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '0 20px 10px', fontSize: 12.5, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
            <i className="ti ti-bulb" style={{ fontSize: 15, flexShrink: 0, color: LEARN_FG }} aria-hidden />
            <span>{data.lessons ? t('ds_sim_lessons', { n: data.lessons }) : t('ds_sim_lessons_none')}</span>
          </div>
        )}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {err !== null ? <div style={{ ...note, color: '#C0392B' }}>{err || t('ds_sim_load_err')}</div>
            : !data ? <div style={note}>{t('ds_sim_loading')}</div>
            : !data.rows.length ? <div style={note}>{t('ds_sim_empty')}</div>
            : (
              <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th style={th}>{t('ds_sim_col_date')}</th>
                    <th style={th}>{t('ds_sim_col_file')}</th>
                    <th style={th}>{t('ds_sim_col_verdict')}</th>
                    <th style={th}>{t('ds_sim_col_changes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => {
                    const ok = r.verdict === 'correct';
                    return (
                      <tr key={r.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {fmtDateTime(r.at)}
                          <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('ds_sim_by', { name: r.by_name || '—' })}</div>
                        </td>
                        <td style={{ ...td, overflowWrap: 'anywhere' }}>
                          {r.file}
                          {r.final?.name && <div title={t('ds_wiz_saved_as')} style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{r.final.name}</div>}
                        </td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 5, padding: '1px 7px', color: ok ? '#15803D' : '#92400E', background: ok ? '#E7F6EE' : '#FEF3C7' }}>{t(ok ? 'ds_sim_ok' : 'ds_sim_fixed')}</span>
                          {r.teaching && (
                            <div style={{ marginTop: 4 }}>
                              <span title={t('ds_sim_learning_tip')} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 700, borderRadius: 5, padding: '1px 6px', color: LEARN_FG, background: '#E6F0FE' }}>
                                <i className="ti ti-bulb" style={{ fontSize: 11 }} aria-hidden />{t('ds_sim_learning')}
                              </span>
                            </div>
                          )}
                        </td>
                        <td style={{ ...td, overflowWrap: 'anywhere' }}>
                          {(r.changed || []).length ? (r.changed || []).map(f => (
                            <div key={f}>
                              <span style={{ color: 'var(--text-secondary)' }}>{fieldName(f, r)}:</span> {valueOf(f, r.ai)} → <b>{valueOf(f, r.final)}</b>
                            </div>
                          )) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
        </div>
        <Pagination page={page} total={data?.total || 0} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
      </div>
    </div>
  );
}

export default function UploadWizard({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [dateKind, setDateKind] = useState<'expiry' | 'issue' | 'none' | ''>('');
  const [multi, setMulti] = useState<'' | 'no' | 'yes'>('no');   // 'no' = one file; 'yes' = dropzone, count is automatic
  const [entries, setEntries] = useState<Entry[]>([]);            // one row per picked file (2026-08-25 rework: no fixed slots)
  const [fileErrs, setFileErrs] = useState<string[]>([]);         // rejections from the LAST pick (size / count cap)
  const [analyzing, setAnalyzing] = useState('');                 // file name the AI is currently reading
  const [compressing, setCompressing] = useState('');             // file name being auto-compressed
  const [dragOver, setDragOver] = useState(false);
  const queueRef = useRef<{ uid: string; file: File }[]>([]);     // AI reads ONE file at a time
  const busyRef = useRef(false);
  const uidRef = useRef(1);
  const [types, setTypes] = useState<string[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  // pump() runs in a long async loop — read the latest lists (and date kind) through refs, not stale closures.
  const typesRef = useRef<string[]>([]); typesRef.current = types;
  const companiesRef = useRef<string[]>([]); companiesRef.current = companies;
  const dateKindRef = useRef(dateKind); dateKindRef.current = dateKind;
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const [confirming, setConfirming] = useState(false);   // "check name + folder" gate before the save (owner 2026-08-25)
  // Calendar step (owner 2026-08-25 v2): NOTHING is created blindly — the operator picks the office
  // calendar and edits the per-file plan (reminder lead days, expiry, birthday) before saving.
  const [confirmStep, setConfirmStep] = useState<1 | 2>(1);
  const [calTarget, setCalTarget] = useState('');   // REQUIRED, starts empty (owner 2026-08-25) — no silent default
  const calChoices = useOfficeMailboxes().calendars;   // the server's own list (expiryEvents calChoices): legal firm first
  // std = a STANDARD lead served from the server's schedule table — rendered read-only, because
  // the standard is not the operator's to adjust (owner 2026-09-16). Hand-added reminders are not
  // std and stay fully editable.
  type PlanRem = { on: boolean; days: number; note: string; std?: boolean };
  type Plan = { known: boolean; includeExpiry: boolean; birthday: boolean; dobOk: boolean; reminders: PlanRem[] };
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planLoading, setPlanLoading] = useState(false);
  const remDate = (expiryIso: string, days: number): string => {
    const d = new Date(`${expiryIso}T00:00:00Z`); d.setUTCDate(d.getUTCDate() - Math.max(0, Math.round(days) || 0));
    return d.toISOString().slice(0, 10);
  };
  const setPlan = (i: number, patch: Partial<Plan> | ((pl: Plan) => Plan)) =>
    setPlans(prev => prev.map((pl, j) => (j === i ? (typeof patch === 'function' ? patch(pl) : { ...pl, ...patch }) : pl)));
  const openCalendarStep = async () => {
    setPlanLoading(true);
    try {
      const r: any = await api.docStorageExpiryPlan(filled.map(e => ({ docType: e.type, expiryIso: entryDate(e), dob: e.ai?.dob || '' })));
      setPlans((r.plans || []).map((pl: any) => ({
        known: !!pl.known, includeExpiry: true, birthday: !!pl.birthday, dobOk: !!pl.dobOk,
        reminders: (pl.reminders || []).map((rm: any) => ({ on: true, days: Number(rm.days) || 7, note: String(rm.note || ''), std: !!rm.std })),
      })));
      setConfirmStep(2);
    } catch (e: any) { setErr(e?.message || 'Could not load the reminder plan'); }
    finally { setPlanLoading(false); }
  };
  const [done, setDone] = useState<{ names: string[]; folderUrl: string; calUrl: string; calendar: string; events: { date: string; summary: string }[] } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Simulation check (report 88c27aca): a tick box for admins only, in the footer row left of Cancel.
  // While it is ticked the green button is "Confirm test" (recordSim below): no file is saved and no
  // calendar event is made. Everyone else keeps the normal flow.
  const isAdmin = useStore(s => s.me?.role === 'admin');
  const [simOn, setSimOn] = useState(false);
  const sim = isAdmin && simOn;
  const [simTotal, setSimTotal] = useState(0);           // tests recorded so far, for "Results (N)"
  const [simNote, setSimNote] = useState('');            // "Test recorded", once a test is confirmed
  const [simResults, setSimResults] = useState(false);   // the Results list is open
  // Admin only (Calem, report 67d7bc3b): a second tick box beside the Simulation check, an admin's alone
  // (isAdmin — users.role, never a name). Ticked, every file of this save is invisible to the rest of the
  // team from the moment it lands: gone from the folder, the search, Quick Access and the mounted drive,
  // and no preview or download (server/src/services/adminOnlyDocs.ts). The server asks the role again.
  const [adminOnly, setAdminOnly] = useState(false);

  // A new date kind keeps the files — pickDateKind re-fills their dates instead (report 280d8c4d).
  useEffect(() => { setEntries([]); setFileErrs([]); queueRef.current = []; }, [multi]);

  useEffect(() => {
    api.docStorageFolders().then((r: any) => setFolders(r.folders || [])).catch(() => {});
    api.docStorageTypes().then((r: any) => setTypes(r.types || [])).catch(() => {});
    api.docStorageCompanies().then((r: any) => setCompanies(r.companies || [])).catch(() => {});
  }, []);
  // Only admins may read the results; anyone else's call would be refused (and alert the admins).
  useEffect(() => {
    if (isAdmin) api.docStorageSimChecks(1, 25).then((r: any) => setSimTotal(Number(r.total) || 0)).catch(() => {});
  }, [isAdmin]);
  // An admin's Rename / Remove in the type picker (report b7482e45): the list is read again, and a card
  // holding that type follows — renamed with it, or emptied once it is removed, as a type comes from the list.
  // An Add (from = '') moves no card: the picker picks the new entry on its own card.
  const typeEdited = (from: string, to: string) => {
    if (from) setEntries(prev => prev.map(en => (en.type === from ? { ...en, type: to } : en)));
    api.docStorageTypes().then((r: any) => setTypes(r.types || [])).catch(() => {});
  };

  const paths = useMemo(() => {
    const byId: Record<string, Folder> = Object.fromEntries(folders.map(f => [f.id, f]));
    const out: Record<string, string> = {};
    for (const f of folders) {
      const parts = [f.name]; let cur = byId[f.parent_id]; let n = 0;
      while (cur && n++ < 12) { parts.unshift(cur.name); cur = byId[cur.parent_id]; }
      out[f.id] = parts.join(' / ');
    }
    return out;
  }, [folders]);
  const allFolders = useMemo(() => {
    const all = folders.map(f => ({ id: f.id, path: paths[f.id] || f.name }));
    all.sort((a, b) => a.path.localeCompare(b.path));
    return all;
  }, [folders, paths]);
  // Per-card options: that card's filter + ITS AI suggestion pinned first (point 5, per file now).
  const folderOptsFor = (e: Entry) => {
    const q = (e.folderQ || '').toLowerCase().trim();
    let all = q ? allFolders.filter(o => o.path.toLowerCase().includes(q)) : allFolders;
    if (e.sugId) {
      const sug = all.find(o => o.id === e.sugId);
      all = sug ? [sug, ...all.filter(o => o.id !== e.sugId)] : all;
    }
    return all;
  };

  const applyAi = (e: Entry, r: Analysis): Entry => {
    const comp = matchToList(r.companyName || '', companiesRef.current);
    const t = matchToList(r.docType || '', typesRef.current);
    // Calem 2026-09-25 (report 280d8c4d, "allow AI to pick the date but the user must confirm"):
    // a still-empty date box gets the date the AI read, unconfirmed — the file can't be saved until
    // the operator ticks "I have checked this date" or picks a date by hand.
    const aiDate = e.dateTxt ? '' : aiDateFor(r, dateKindRef.current);
    const type = e.type || t;
    return {
      ...e, ai: r,
      person: r.personName || '',
      company: comp || (r.companyName ? '' : ''),
      companyGuessed: false,
      companyHint: r.companyName && !comp ? r.companyName : '',
      type,
      typeHint: !t && r.docType ? r.docType : '',
      sugId: r.suggestedFolderId || '',
      folderId: e.folderId || r.suggestedFolderId || '',
      // Report c8ad7a51: the number the AI read off the document, ready to be checked. The box shows
      // for the types that carry one, so a number read on any other document stays out of sight.
      docNo: e.docNo || aiNoFor(type, r),
      dateTxt: e.dateTxt || aiDate,
      dateConfirmed: aiDate ? false : e.dateConfirmed,
    };
  };
  // The AI reads BOTH dates, so switching the date kind keeps the files and re-fills each box from
  // the other date the AI read — unconfirmed again, since it is a different date.
  const pickDateKind = (v: 'expiry' | 'issue' | 'none') => {
    setDateKind(v);
    setEntries(prev => prev.map(en => ({ ...en, dateTxt: aiDateFor(en.ai, v), dateConfirmed: false })));
  };

  // ── The Company from the person (Calem, report b7482e45, 4th opening) ──
  // A personal document such as a 90 Day Report seldom prints the company, so the AI's read leaves the box
  // empty. Once a card has a person name — after the AI's read, and half a second after the name is typed —
  // the server looks for the company in that person's Active and Closed jobs and Our Documents files
  // (GET /doc-storage/company-guess). Its answer fills a Company box that is still empty, under an amber
  // note to check it, and a guessed company follows a new name (emptied when the new one finds none). Once
  // the operator has picked a company, or "No company (personal document)", which a guess never picks, the
  // card is left alone, as is a company the AI read.
  const guessedFor = useRef<Record<string, string>>({});   // card uid → the person name last looked up
  useEffect(() => {
    const guessable = (e: Entry) => !e.companyByHand && (!e.company || !!e.companyGuessed);
    const due = entries.filter(e => guessable(e) && (!!e.person.trim() || !!e.companyGuessed) && guessedFor.current[e.uid] !== e.person.trim());
    if (!due.length) return;
    const timer = setTimeout(() => {
      for (const e of due) {
        const person = e.person.trim();
        guessedFor.current[e.uid] = person;
        (person ? api.docStorageCompanyGuess(person) : Promise.resolve({ company: '' }))
          .then(r => {
            const company = String(r?.company || '');
            setEntries(prev => prev.map(en => (en.uid === e.uid && en.person.trim() === person && guessable(en) ? { ...en, company, companyGuessed: !!company } : en)));
          })
          .catch(() => { /* no guess: the box stays as it is */ });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [entries]);

  // Dropzone model (owner 2026-08-25 rework): drag & drop or multi-select — pick twelve PND 51s in
  // one go, the count is automatic. Oversize images and PDFs are auto-compressed (COMPRESS_OVER /
  // INTAKE_MAX above), MAX_FILES per upload. The AI reads the files
  // ONE AT A TIME through a queue; a row removed mid-queue is simply skipped.
  const MAX_FILES = 20;
  const setEntryByUid = (uid: string, patch: Partial<Entry> | ((e: Entry) => Entry)) =>
    setEntries(prev => prev.map(en => (en.uid === uid ? (typeof patch === 'function' ? patch(en) : { ...en, ...patch }) : en)));
  const pump = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    for (;;) {
      const job = queueRef.current.shift();
      if (!job) break;
      setAnalyzing(job.file.name);
      try {
        const r: any = await api.docStorageAnalyze(job.file, dateKind || 'none');
        setEntryByUid(job.uid, en => applyAi(en, r));
        delete guessedFor.current[job.uid];   // the name the AI read gets its own company guess
      } catch (e: any) {
        setEntryByUid(job.uid, { aiErr: e?.message || 'Analysis failed — fill by hand.' });
      }
    }
    setAnalyzing(''); busyRef.current = false;
  };
  const addFiles = async (list: FileList | File[] | null | undefined, replace = false) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const errs: string[] = []; const ok: { f: File; origMb?: string }[] = [];
    const room = replace ? 1 : MAX_FILES - entries.length;
    for (const f of files) {
      if (ok.length >= room) { errs.push(`"${f.name}" skipped — at most ${replace ? 1 : MAX_FILES} file(s) per upload.`); continue; }
      if (f.size > INTAKE_MAX) { errs.push(`"${f.name}" is ${mbOf(f.size)} MB — the limit is 20 MB.`); continue; }
      if (f.size <= COMPRESS_OVER) { ok.push({ f }); continue; }
      // Over 5 MB → made smaller automatically, no bouncing back to the operator: images in the
      // browser, PDFs on the server. Nothing smaller came back → the original is kept.
      const isPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
      setCompressing(f.name);
      try {
        const smaller = isPdf ? await shrinkPdf(f) : await compressImage(f);
        ok.push(smaller ? { f: smaller, origMb: mbOf(f.size) } : { f });
      }
      catch { ok.push({ f }); }
      finally { setCompressing(''); }
    }
    setFileErrs(errs); setErr(''); setSimNote('');
    if (!ok.length) return;
    const fresh: Entry[] = ok.map(x => ({ uid: `f${uidRef.current++}`, file: x.f, origMb: x.origMb, ai: null, aiErr: '', person: '', company: '', companyHint: '', typeHint: '', dateTxt: '', dateConfirmed: false, type: '', sugId: '', folderId: '' }));
    if (replace) { queueRef.current = []; setEntries(fresh); } else { setEntries(prev => [...prev, ...fresh]); }
    queueRef.current.push(...fresh.map(e => ({ uid: e.uid, file: e.file })));
    void pump();
  };
  const removeEntry = (uid: string) => {
    queueRef.current = queueRef.current.filter(j => j.uid !== uid);
    setEntries(prev => prev.filter(e => e.uid !== uid));
  };

  const setEntry = (i: number, patch: Partial<Entry>) => setEntries(prev => prev.map((e, j) => (j === i && e ? { ...e, ...patch } : e)));
  // A type picked by hand brings its own number rule with it (report c8ad7a51), so whatever is in the
  // Number box is kept only as far as the new type allows.
  const setType = (i: number, type: string) => setEntries(prev => prev.map((e, j) => (j === i && e ? { ...e, type, docNo: retypeNo(e.docNo || '', type) } : e)));
  // "Apply the same:" bubbles above the file cards (report ebd7e8e6): one of file 1's fields over every other file.
  // The number itself is never copied — each document has its own — but a type copied over takes its rule along.
  const applyToOthers = (patch: Partial<Entry>) => setEntries(prev => prev.map((e, j) => {
    if (j === 0) return e;
    const next = { ...e, ...patch };
    return patch.type === undefined ? next : { ...next, docNo: retypeNo(e.docNo || '', next.type) };
  }));
  const file1 = entries[0];

  const entryDate = (e: Entry) => (dateKind === 'none' ? todayStr() : dmyToIso(e.dateTxt));
  const entryName = (e: Entry) => {
    const comp = e.company && e.company !== NONE ? e.company : '';
    const d = entryDate(e);
    const ext = e.file.name.split('.').pop() || 'pdf';
    // A document's number sits right after the type word (report c8ad7a51) — "Chanote 53658 (…)".
    const no = noRuleFor(e.type) && e.docNo ? ` ${e.docNo}` : '';
    return e.type && (e.person || comp) && d ? `${clean(e.type)}${no}${e.person ? ` (${clean(e.person)})` : ''}${comp ? ` ${clean(comp)}` : ''} ${d}.${ext}` : '';
  };
  // company must be CHOSEN (may be "No company"); each file now needs its own type + folder too —
  // and a dated file needs its date CONFIRMED by the operator (report 280d8c4d).
  const entryValid = (e: Entry) => !!entryName(e) && !!e.company && !!e.type && !!e.folderId && (dateKind === 'none' || e.dateConfirmed);
  const filled = entries;
  const canSave = filled.length > 0 && filled.every(entryValid) && !saving && !analyzing;

  const save = async () => {
    if (!canSave) return;
    setSaving(true); setErr('');
    const savedNames: string[] = [];
    const savedIds: string[] = [];
    try {
      for (const e of filled) {
        const renamed = new File([e.file], entryName(e), { type: e.file.type });
        const up: any = await api.filesUpload('personal', e.folderId, renamed);
        savedNames.push(entryName(e));
        if (up?.id) savedIds.push(String(up.id));
      }
      // "Admin only" ticked: the files are in the archive now, so this is the step that takes them out of
      // the team's sight. It is NOT best-effort like the calendar below — if it fails, the documents are
      // sitting in Our Documents for everyone to read, so the wizard says so and stops instead of showing
      // the success screen.
      if (adminOnly && savedIds.length) {
        try { await api.docStorageAdminOnly(savedIds); }
        catch { setErr(t('ds_admin_only_err', { n: savedIds.length })); setSaving(false); return; }
      }
      const folderUrl = `/m/doc_storage?folder=${encodeURIComponent(filled[0].folderId)}&focus=${encodeURIComponent(savedNames[0] || '')}`;
      const firstExpiry = dateKind === 'expiry' ? entryDate(filled[0]) : '';
      const calUrl = firstExpiry ? `/m/calendar?date=${firstExpiry}` : '';
      // Expiry-dated documents auto-create TEAM calendar events per the owner's EXPIRY DATE LIST
      // (expiry day + type-specific reminders + passport birthdays). Best-effort — the files are
      // already saved, so a calendar hiccup must not fail the wizard.
      let events: { date: string; summary: string }[] = []; let calendar = '';
      if (dateKind === 'expiry') {
        try {
          const r: any = await api.docStorageExpiryEvents({
            calendar: calTarget,
            items: filled.map((e, i) => ({
              docType: e.type, person: e.person, expiryIso: entryDate(e), dob: e.ai?.dob || '',
              includeExpiry: plans[i] ? plans[i].includeExpiry : true,
              birthday: !!plans[i]?.birthday,
              reminders: (plans[i]?.reminders || []).filter(rm => rm.on && rm.days > 0).map(rm => ({ days: rm.days, note: rm.note })),
            })),
          });
          events = r.created || []; calendar = r.calendar || '';
        } catch { /* best-effort */ }
      }
      // Auto pop-ups (points 6 + 8). Popup blockers may eat these — the success screen keeps buttons.
      try { window.open(folderUrl, '_blank'); } catch { /* blocked */ }
      if (calUrl) { try { window.open(calUrl, '_blank'); } catch { /* blocked */ } }
      setDone({ names: savedNames, folderUrl, calUrl, calendar, events });
      onSaved();
    } catch (e: any) {
      setErr(`${e?.message || 'Save failed'}${savedNames.length ? ` — ${savedNames.length} file(s) were already saved` : ''}`);
      setSaving(false);
    }
  };

  // "Confirm test" (simulation check): for each file card, what the AI read and the file name its read
  // makes, next to the final fields after the admin's edits and the "Will be saved as" name, plus which
  // of type, person, company, date and folder were changed. The only call is POST
  // /doc-storage/sim-checks (no upload, no calendar events, no confirmation step); the tester and the
  // time come from the server. Then the wizard empties for the next file.
  const recordSim = async () => {
    if (!sim || !canSave) return;
    setSaving(true); setErr('');
    const same = (x: string, y: string) => (x || '').replace(/\s+/g, ' ').trim() === (y || '').replace(/\s+/g, ' ').trim();
    try {
      const items = filled.map(e => {
        const a = e.ai;
        // The card as the AI's read fills it in (applyAi): the list's type and company, the date for this kind.
        const aiType = matchToList(a?.docType || '', types) || a?.docType || '';
        const aiCompany = matchToList(a?.companyName || '', companies);
        const aiDmy = aiDateFor(a, dateKind);
        const finalCompany = e.company === NONE ? '' : e.company;
        const changed = [
          !same(aiType, e.type) && 'type',
          !same(a?.personName || '', e.person) && 'person',
          !same(aiCompany || a?.companyName || '', finalCompany) && 'company',
          dateKind !== 'none' && dmyToIso(aiDmy) !== entryDate(e) && 'date',
          (a?.suggestedFolderId || '') !== e.folderId && 'folder',
        ].filter(Boolean);
        return {
          file: e.file.name, dateKind,
          ai: {
            type: a?.docType || '', person: a?.personName || '', company: a?.companyName || '',
            date: (dateKind === 'expiry' ? a?.expiryDate : dateKind === 'issue' ? a?.issueDate : '') || '',
            folderId: a?.suggestedFolderId || '', folder: paths[a?.suggestedFolderId || ''] || '',
            name: entryName({ ...e, type: aiType, person: a?.personName || '', company: aiCompany, dateTxt: aiDmy, docNo: aiNoFor(aiType, a) }),
          },
          final: {
            type: e.type, person: e.person, company: finalCompany, date: dateKind === 'none' ? '' : entryDate(e),
            folderId: e.folderId, folder: paths[e.folderId] || '', name: entryName(e),
          },
          changed,
        };
      });
      const r: any = await api.docStorageSimCheckAdd(items);
      setSimTotal(Number(r.total) || 0);
      setSimNote(t('ds_sim_recorded', { n: items.length }));
      queueRef.current = []; setEntries([]); setFileErrs([]);
    } catch (e: any) { setErr(e?.message || t('ds_sim_save_err')); }
    finally { setSaving(false); }
  };
  // "Admin only": at the left of the footer row, in front of the Simulation check it stands beside. It is
  // only there while there are files to save — unlike the Simulation check it says nothing on its own — and
  // it never shows for anybody but an admin, so the rest of the team has no tick box at all.
  const adminOnlyBox = isAdmin && (
    <label title={t('ds_admin_only_tip')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: adminOnly ? 'var(--accent)' : 'var(--text-secondary)' }}>
      <input type="checkbox" checked={adminOnly} disabled={saving} onChange={() => setAdminOnly(v => !v)} />
      {t('ds_admin_only')}
    </label>
  );
  // The tick box and the Results link: at the left of the footer row, or of their own row with no file.
  const simControls = isAdmin && (
    <>
      <label title={t('ds_sim_check_tip')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', color: sim ? 'var(--accent)' : 'var(--text-secondary)' }}>
        <input type="checkbox" checked={simOn} disabled={saving} onChange={() => { setSimOn(v => !v); setSimNote(''); }} />
        {t('ds_sim_check')}
      </label>
      <button type="button" onClick={() => setSimResults(true)} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontSize: 12, fontWeight: 600, textDecoration: 'underline' }}>
        {t('ds_sim_results', { n: simTotal })}
      </button>
      <span style={{ flex: 1 }} />
    </>
  );

  const dateCheckFor = (e: Entry) => {
    if (dateKind === 'none' || !e.dateTxt) return null;
    const iso = dmyToIso(e.dateTxt);
    // The box takes typing (report b7c020ac): a half-typed or impossible date says so, AI read or not.
    if (!iso) return { tone: '#B45309', text: t('ds_wiz_date_invalid') };
    if (!e.ai) return null;
    const aiIso = dateKind === 'expiry' ? (e.ai.expiryDate || '') : (e.ai.issueDate || '');
    if (!aiIso) return { tone: '#B45309', text: 'AI could not read this date from the document — please double-check it yourself.' };
    return aiIso === iso
      ? { tone: '#15803D', text: `Matches the document (AI read ${isoToDmy(aiIso)}).` }
      : { tone: '#C0392B', text: `AI read ${isoToDmy(aiIso)} from the document — please double-check before saving.` };
  };

  // ── Success screen (points 6 + 8) ──
  if (done) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
        <div style={{ width: 520, maxWidth: '94vw', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 24, boxShadow: 'var(--shadow-md)' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#166534', marginBottom: 8 }}><i className="ti ti-circle-check" style={{ marginRight: 7 }} />Saved to the archive</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>{done.names.length} file(s):</div>
          <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: 12.5, background: 'var(--bg-app)', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
            {done.names.map((n, i) => <div key={i} style={{ padding: '2px 0' }}>{n}</div>)}
          </div>
          {done.events.length > 0 && (
            <>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 6 }}>
                <i className="ti ti-calendar-plus" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden />
                {done.events.length} calendar event(s) created in <b>{done.calendar}</b>:
              </div>
              <div style={{ maxHeight: 150, overflowY: 'auto', fontSize: 12, background: 'var(--bg-app)', borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
                {done.events.map((ev, i) => (
                  <div key={i} style={{ padding: '2px 0', display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--text-tertiary)', fontFamily: 'ui-monospace, monospace', flexShrink: 0 }}>{dmyEn(ev.date)}</span>
                    <span>{ev.summary}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => window.open(done.folderUrl, '_blank')} style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              <i className="ti ti-folder-open" style={{ marginRight: 6 }} />Open folder</button>
            {done.calUrl && (
              <button onClick={() => window.open(done.calUrl, '_blank')} style={{ padding: '9px 16px', borderRadius: 10, border: '1px solid var(--accent)', background: 'transparent', color: 'var(--accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                <i className="ti ti-calendar-event" style={{ marginRight: 6 }} />Open calendar at expiry</button>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !saving && !analyzing) onClose(); }}>
      <div style={{ width: 620, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 4 }}><i className="ti ti-archive" style={{ marginRight: 6 }} />Upload document</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 10 }}>Naming: Type (Person Name) Company — AI fills the fields and the date, YOU confirm the date.</div>

        <label style={lbl}>① Does this document have…</label>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {([['expiry', 'An EXPIRY date'], ['issue', 'An ISSUE date (no expiry)'], ['none', 'Neither']] as const).map(([v, t]) => (
            <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input type="radio" name="datekind" checked={dateKind === v} onChange={() => pickDateKind(v)} />{t}
            </label>
          ))}
        </div>

        {dateKind && (
          <>
            <label style={lbl}>② Want to store more than one of the same type (e.g. PND 51)?</label>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              {([['no', 'No — just one'], ['yes', 'Yes — several']] as const).map(([v, t]) => (
                <label key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="radio" name="multi" checked={multi === v} onChange={() => { setMulti(v); setEntries([]); }} />{t}
                </label>
              ))}

            </div>
          </>
        )}

        {dateKind && multi && (
          <>
            <label style={lbl}>③ {t(multi === 'yes' ? 'ds_wiz_file_many' : 'ds_wiz_file_one')}</label>
            {multi === 'no' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 9, border: '1px dashed var(--border-strong)', background: entries[0] ? 'var(--bg-app)' : 'var(--c-gray-50)', color: entries[0] ? 'var(--text-secondary)' : 'var(--accent)', fontWeight: 700, fontSize: 12.5, cursor: 'pointer' }}>
                  <i className={`ti ${entries[0] ? 'ti-refresh' : 'ti-upload'}`} style={{ fontSize: 14 }} />
                  Choose file{entries[0] ? ' — replace' : ''}
                  <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,image/*,application/pdf"
                    onChange={e => { void addFiles(e.target.files, true); if (e.currentTarget) e.currentTarget.value = ''; }} />
                </label>
                {entries[0] && <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{entries[0].file.name} <span style={{ color: 'var(--text-tertiary)' }}>({(entries[0].file.size / 1024 / 1024).toFixed(2)} MB)</span>{entries[0].origMb && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#15803D', background: '#E7F6EE', borderRadius: 5, padding: '0 6px' }}>compressed · was {entries[0].origMb} MB</span>}</span>}
              </div>
            ) : (
              <>
                {/* ONE dropzone instead of N slot buttons (owner 2026-08-25): drop or multi-select,
                    the count is automatic, more files can be added any time. */}
                <label onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); void addFiles(e.dataTransfer?.files); }}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '16px 12px', borderRadius: 10, border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border-strong)'}`, background: dragOver ? 'var(--bg-active)' : 'var(--c-gray-50)', cursor: 'pointer', textAlign: 'center' }}>
                  <i className="ti ti-cloud-upload" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{entries.length ? '+ Add more files' : 'Drop files here — or click to choose several at once'}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>The number of files is automatic — no need to count first</span>
                  <input type="file" hidden multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,image/*,application/pdf"
                    onChange={e => { void addFiles(e.target.files); if (e.currentTarget) e.currentTarget.value = ''; }} />
                </label>
                {entries.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {entries.map((en, i) => (
                      <div key={en.uid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 9px', borderRadius: 7, background: 'var(--bg-app)', border: '1px solid var(--border)', fontSize: 12 }}>
                        <span style={{ color: 'var(--text-tertiary)', fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span>
                        <i className="ti ti-file" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>{en.file.name}</span>
                        <span style={{ color: 'var(--text-tertiary)', flexShrink: 0 }}>({(en.file.size / 1024 / 1024).toFixed(2)} MB)</span>
                        {en.origMb && <span title={`Automatically compressed from ${en.origMb} MB`} style={{ fontSize: 10, fontWeight: 800, color: '#15803D', background: '#E7F6EE', borderRadius: 5, padding: '0 6px', flexShrink: 0, whiteSpace: 'nowrap' }}>compressed · was {en.origMb} MB</span>}
                        <span style={{ flex: 1 }} />
                        {en.ai ? <i className="ti ti-circle-check" title="AI has read this file" style={{ color: '#15803D', fontSize: 15, flexShrink: 0 }} aria-hidden />
                          : en.aiErr ? <i className="ti ti-alert-triangle" title={en.aiErr} style={{ color: '#B45309', fontSize: 15, flexShrink: 0 }} aria-hidden />
                          : <i className="ti ti-loader" style={{ color: '#DB2777', fontSize: 15, flexShrink: 0, animation: 'spin 1s linear infinite', display: 'inline-block' }} aria-hidden />}
                        <button onClick={() => removeEntry(en.uid)} title="Remove this file" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-tertiary)', display: 'inline-flex', flexShrink: 0 }}>
                          <i className="ti ti-x" style={{ fontSize: 14 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
            {fileErrs.map((m, i) => <div key={i} style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: '#C0392B' }}>{m}</div>)}
            {/* Owner 2026-08-25: big and PINK — the operator must notice the AI is still reading. */}
            {compressing && (
              <div style={{ marginTop: 10, fontSize: 14, fontWeight: 800, color: '#DB2777' }}>
                <i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: 7, fontSize: 15 }} />Compressing {compressing}…
              </div>
            )}
            {analyzing && (
              <div style={{ marginTop: 10, fontSize: 16, fontWeight: 800, color: '#DB2777' }}>
                <i className="ti ti-loader" style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginRight: 8, fontSize: 17 }} />
                AI is reading {analyzing}…{entries.filter(x => !x.ai && !x.aiErr).length > 1 ? ` (${entries.filter(x => !x.ai && !x.aiErr).length} files left)` : ''}
              </div>
            )}
          </>
        )}

        {filled.length > 0 && (
          <>
            <label style={lbl}>④ Check each file — type · name · company · date · folder</label>
            {/* Batch shortcuts (Calem, report ebd7e8e6, 2nd opening: "under Check Each File and not
                inside the gray box"): "Apply the same:" then one bubble per field, each copying only
                that field of file 1 over the other files, so any one or more can be used. A bubble
                stays greyed out until file 1 has that field; with no date (① "Neither") there is no
                Date bubble. Person names stay individual — and so does a title deed's number, which
                Type never carries over (report c8ad7a51); the date carries its confirmed tick along. */}
            {entries.length > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', marginRight: 2 }}>{t('ds_wiz_apply_heading')}</span>
                {([
                  [!!file1.company, 'ds_wiz_apply_company', { company: file1.company, companyGuessed: false, companyByHand: true }],
                  [!!dmyToIso(file1.dateTxt), 'ds_wiz_apply_date', { dateTxt: file1.dateTxt, dateConfirmed: file1.dateConfirmed }],
                  [!!file1.type, 'ds_wiz_apply_type', { type: file1.type }],
                  [!!file1.folderId, 'ds_wiz_apply_folder', { folderId: file1.folderId }],
                ] as [boolean, string, Partial<Entry>][]).filter(b => dateKind !== 'none' || b[1] !== 'ds_wiz_apply_date').map(([on, key, patch]) => (
                  <button key={key} onClick={() => applyToOthers(patch)} disabled={!on} title={t(on ? 'ds_wiz_apply_tip' : 'ds_wiz_apply_off')}
                    style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${on ? 'var(--accent)' : 'var(--border-strong)'}`, background: 'transparent', color: on ? 'var(--accent)' : 'var(--text-tertiary)', fontWeight: 700, fontSize: 12, cursor: on ? 'pointer' : 'default' }}>
                    {t(key)}
                  </button>
                ))}
              </div>
            )}
            {entries.map((e, i) => {
              const chk = dateCheckFor(e);
              const saveAs = entryName(e);
              const noRule = noRuleFor(e.type);
              return (
                <div key={e.uid} style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', background: multi === 'yes' ? 'var(--bg-app)' : 'transparent' }}>
                  {multi === 'yes' && <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}><i className="ti ti-file" style={{ marginRight: 5 }} />File {i + 1} · {e.file.name}</div>}
                  {e.aiErr && <div style={{ fontSize: 11.5, color: '#B45309', marginBottom: 6 }}>{e.aiErr}</div>}
                  {e.ai?.notes && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 6 }}>AI note: {e.ai.notes} (confidence: {e.ai.confidence})</div>}
                  <div style={{ marginBottom: 8 }}>
                    {/* Calem 2026-10-06, widened 2026-10-07 (report c8ad7a51): a document that carries
                        its own number — a title deed, a passport, an ID card, a bank, house or vehicle
                        book, a company's papers, a permit or a licence — gets a small Number box BESIDE
                        the type, where he marked it, taking only the characters its own number is printed
                        with (NO_RULES; the rule caps the box, so a pasted number is cleaned, not cut).
                        The two labels share the first grid row so both boxes line up. */}
                    <div style={{ display: 'grid', gridTemplateColumns: noRule ? `minmax(0, 1fr) ${noBoxW(noRule)}px` : 'minmax(0, 1fr)', columnGap: 8, alignItems: 'end' }}>
                      <div style={{ gridRow: 1, fontSize: 10, color: 'var(--text-tertiary)' }}>Document type{e.type && e.ai && matchToList(e.ai.docType || '', types) === e.type ? ' (AI)' : ''}</div>
                      {/* Admins edit the list from here (report b7482e45); everyone else keeps the plain drop-down. */}
                      <div style={{ gridRow: 2, alignSelf: 'start' }}>
                        {isAdmin ? <TypePicker value={e.type} types={types} onPick={v => setType(i, v)} onEdited={typeEdited} /> : (
                          <select value={e.type} onChange={ev => setType(i, ev.target.value)} style={inp}>
                            <option value="">Please select…</option>
                            {types.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                          </select>
                        )}
                      </div>
                      {noRule && (
                        <>
                          <div style={{ gridRow: 1, fontSize: 10, color: 'var(--text-tertiary)' }}>{t('ds_wiz_doc_no')}</div>
                          <div style={{ gridRow: 2, alignSelf: 'start' }}>
                            <input value={e.docNo || ''} inputMode={noRule.numeric ? 'numeric' : 'text'} autoComplete="off" placeholder={t('ds_wiz_deed_no_ph')}
                              onChange={ev => setEntry(i, { docNo: retypeNo(ev.target.value, e.type) })} style={inp} />
                          </div>
                        </>
                      )}
                    </div>
                    {e.typeHint && !e.type && <div style={{ marginTop: 3, fontSize: 11, color: '#B45309' }}>AI suggested “{e.typeHint}” — pick the closest from the list.</div>}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <div><div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{t('ds_wiz_person')}</div>
                      <input value={e.person} onChange={ev => setEntry(i, { person: ev.target.value })} placeholder={t('ds_wiz_person_ph')} style={inp} /></div>
                    <div><div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Company</div>
                      <CompanyPicker value={e.company} companies={companies} onPick={v => setEntry(i, { company: v, companyGuessed: false, companyByHand: true })} />
                      {/* Report b7482e45: a company guessed from the person's jobs / files, as the AI's date is flagged. */}
                      {e.companyGuessed && (
                        <div style={{ marginTop: 4, fontSize: 12, fontWeight: 600, color: '#B45309' }}>
                          <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />
                          {t('ds_wiz_company_guessed')}
                        </div>
                      )}
                      {e.companyHint && (!e.company || e.companyGuessed) && <div style={{ marginTop: 3, fontSize: 11, color: '#B45309' }}>AI read “{e.companyHint}” — pick from the list, or add the client first.</div>}
                    </div>
                  </div>
                  {/* Calem 2026-09-25 (report cfa493af): the destination folder sits BESIDE the date,
                      whose box is 25% narrower (190 → 143 px). The two labels share the first grid row
                      so both boxes line up even when the date's label wraps; the folder list opens
                      under the folder. Without a date the folder keeps the whole row. */}
                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: dateKind !== 'none' ? '143px minmax(0, 1fr)' : 'minmax(0, 1fr)', columnGap: 8, alignItems: 'end' }}>
                    {dateKind !== 'none' && (
                      <>
                        <div style={{ gridRow: 1, fontSize: 10, color: 'var(--text-tertiary)' }}>{dateKind === 'expiry' ? 'Expiry' : 'Issue'} date — filled by AI, confirmed by YOU</div>
                        <div style={{ gridRow: 2, alignSelf: 'start' }}>
                          {/* Owner 2026-08-25: a date picker instead of typing DD-MM-YYYY. Calem 2026-09-29
                              (report b7c020ac): the native one showed the browser's month-first order, so
                              it is DD/MM/YYYY text with a calendar button now. The state stays DMY so storage
                              (ISO), previews and the AI double-check are untouched. A date typed or picked by
                              hand counts as confirmed (report 280d8c4d). */}
                          <DayFirstDateInput value={e.dateTxt} onChange={v => setEntry(i, { dateTxt: v, dateConfirmed: true })} />
                        </div>
                      </>
                    )}
                    {/* Per-file destination (owner/Calem 2026-08-26): collapsed row shows the chosen
                        path (AI suggestion by default); click to expand the filterable list. */}
                    <div style={{ gridRow: 1, fontSize: 10, color: 'var(--text-tertiary)' }}>Destination folder{e.sugId && e.folderId === e.sugId ? ' (AI suggestion)' : ''}</div>
                    <div style={{ gridRow: 2, alignSelf: 'start' }}>
                      {!e.folderOpen ? (
                        <div onClick={() => setEntry(i, { folderOpen: true, folderQ: '' })}
                          style={{ ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-surface)' }}>
                          <i className="ti ti-folder" style={{ color: e.folderId ? 'var(--accent)' : '#B45309', flexShrink: 0 }} aria-hidden />
                          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600, color: e.folderId ? 'var(--text-primary)' : '#B45309' }}>
                            {e.folderId ? (paths[e.folderId] || '…') : 'Choose the folder…'}
                          </span>
                          {e.sugId && e.folderId === e.sugId && <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--accent)', flexShrink: 0 }}>AI</span>}
                          <i className="ti ti-chevron-down" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} aria-hidden />
                        </div>
                      ) : (
                        <>
                          <input autoFocus value={e.folderQ || ''} onChange={ev => setEntry(i, { folderQ: ev.target.value })} placeholder="Filter folders…" autoComplete="off" style={{ ...inp, marginBottom: 4 }} />
                          <div style={{ border: '1px solid var(--border-strong)', borderRadius: 8, maxHeight: 150, overflowY: 'auto', background: 'var(--c-gray-50)' }}>
                            {folderOptsFor(e).map(o => {
                              const isSug = o.id === e.sugId;
                              const isSel = o.id === e.folderId;
                              return (
                                <div key={o.id} onClick={() => setEntry(i, { folderId: o.id, folderOpen: false })}
                                  style={{ padding: '6px 10px', fontSize: 12.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                    background: isSel ? 'var(--bg-active)' : 'transparent',
                                    color: isSug ? 'var(--accent)' : 'var(--text-secondary)',
                                    fontWeight: isSug || isSel ? 700 : 400,
                                    opacity: !e.sugId || isSug || isSel ? 1 : 0.55,
                                    borderBottom: '1px solid var(--border)' }}>
                                  {isSel && <i className="ti ti-check" style={{ fontSize: 13, flexShrink: 0 }} />}
                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.path}</span>
                                  {isSug && <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 800, color: 'var(--accent)', flexShrink: 0 }}>AI</span>}
                                </div>
                              );
                            })}
                            {folderOptsFor(e).length === 0 && <div style={{ padding: 10, fontSize: 12, color: 'var(--text-tertiary)' }}>No folder matches the filter.</div>}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Calem 2026-09-26 (report cfa493af, "span across the full window"): the date's
                      checks and its tick box run the full width of the card, under the date/folder
                      row — not down the narrow date column. */}
                  {dateKind !== 'none' && (
                    <div>
                      {chk && <div style={{ marginTop: 4, fontSize: 12, fontWeight: 600, color: chk.tone }}>{chk.text}</div>}
                      {/* Calem 2026-09-25 (report 280d8c4d): the AI's date needs the operator's tick —
                          amber until then, and "Save to archive" stays disabled. */}
                      {dmyToIso(e.dateTxt) && !e.dateConfirmed && (
                        <div style={{ marginTop: 4, fontSize: 12, fontWeight: 600, color: '#B45309' }}>
                          <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />
                          {t('ds_wiz_date_ai_note')}
                        </div>
                      )}
                      {dmyToIso(e.dateTxt) && (
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                          <input type="checkbox" checked={e.dateConfirmed} onChange={() => setEntry(i, { dateConfirmed: !e.dateConfirmed })} />
                          I have checked this date
                        </label>
                      )}
                    </div>
                  )}
                  {/* Calem 2026-09-25 (report cfa493af, "make this a lot more visible"): the final
                      file name in a highlighted box — icon and text amber until the fields are complete. */}
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 9, border: '1.5px solid var(--accent)', background: 'var(--accent-bg)' }}>
                    <i className="ti ti-file-text" style={{ fontSize: 22, color: saveAs ? 'var(--accent)' : '#B45309', flexShrink: 0 }} aria-hidden />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--accent-text)' }}>{t('ds_wiz_saved_as')}</div>
                      <div style={{ marginTop: 1, fontSize: 13.5, fontWeight: 700, color: saveAs ? 'var(--text-primary)' : '#B45309', overflowWrap: 'anywhere' }}>{saveAs || t('ds_wiz_saved_as_pending')}</div>
                    </div>
                  </div>
                </div>
              );
            })}

            {err && <div style={{ marginTop: 8, fontSize: 12, color: '#E24B4A' }}>{err}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 14 }}>
              {adminOnlyBox}
              {simControls}
              <button onClick={() => !saving && onClose()} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              {/* Ticked "Simulation check": the same entryValid gate, but recordSim instead of the confirmation step. */}
              <button onClick={() => { if (!canSave) return; if (sim) void recordSim(); else setConfirming(true); }} disabled={!canSave} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: canSave ? 'var(--accent)' : 'var(--c-gray-100)', color: canSave ? '#fff' : 'var(--text-tertiary)', fontWeight: 700, fontSize: 13, cursor: canSave ? 'pointer' : 'default' }}>
                {sim ? t(saving ? 'ds_sim_recording' : 'ds_sim_confirm') : saving ? 'Saving…' : filled.length > 1 ? `Save ${filled.length} files to archive` : 'Save to archive'}</button>
            </div>

            {/* Confirmation gate (owner 2026-08-25): show exactly WHAT will be saved WHERE — and
                what lands on the team calendar — before anything is written. */}
            {confirming && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }} onMouseDown={e => { if (e.target === e.currentTarget && !saving) { setConfirming(false); setConfirmStep(1); } }}>
                <div style={{ width: confirmStep === 2 ? 600 : 520, maxWidth: '94vw', maxHeight: '86vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, border: '1px solid var(--border)', padding: 20, boxShadow: 'var(--shadow-md)' }}>
                  {confirmStep === 1 ? (
                    <>
                      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}><i className="ti ti-checklist" style={{ marginRight: 6 }} aria-hidden />Please confirm before saving</div>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 4 }}>File name{filled.length > 1 ? 's' : ''} ({filled.length})</div>
                      <div style={{ maxHeight: 190, overflowY: 'auto', fontSize: 12.5, background: 'var(--bg-app)', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
                        {filled.map(e => (
                          <div key={e.uid} style={{ padding: '3px 0' }}>
                            <div style={{ fontWeight: 600 }}>{entryName(e)}</div>
                            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                              <i className="ti ti-folder" style={{ marginRight: 4, color: 'var(--accent)' }} aria-hidden />{paths[e.folderId] || '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Ticked "Admin only": the gate exists to show exactly what is about to happen, and this
                          is the half of it the file names cannot show. */}
                      {adminOnly && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-bg)', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                          <i className="ti ti-lock" style={{ marginRight: 5 }} aria-hidden />{t('ds_admin_only_tip')}
                        </div>
                      )}
                      {dateKind === 'expiry' && (
                        <div style={{ fontSize: 12, color: '#0A8A94', background: '#F0FBFC', border: '1px solid #B6E9EC', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                          <i className="ti ti-calendar-plus" style={{ marginRight: 5 }} aria-hidden />
                          Next step: choose the calendar and review the reminder / expiry events before anything is created.
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <button onClick={() => setConfirming(false)} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Back</button>
                        {dateKind === 'expiry' ? (
                          <button onClick={() => void openCalendarStep()} disabled={planLoading} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                            {planLoading ? 'Loading…' : <>Next: Calendar <i className="ti ti-arrow-right" style={{ marginLeft: 5 }} aria-hidden /></>}
                          </button>
                        ) : (
                          <button onClick={() => { setConfirming(false); void save(); }} style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                            <i className="ti ti-check" style={{ marginRight: 5 }} aria-hidden />Confirm &amp; save
                          </button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}><i className="ti ti-calendar-plus" style={{ marginRight: 6 }} aria-hidden />Calendar events</div>
                      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 4 }}>Save events to <span style={{ color: '#DC2626' }}>*</span></div>
                      <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                        {calChoices.map(em => (
                          <label key={em} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: calTarget === em ? 'var(--accent)' : 'var(--text-secondary)' }}>
                            <input type="radio" name="ds-cal" checked={calTarget === em} onChange={() => setCalTarget(em)} />{em}
                          </label>
                        ))}
                      </div>
                      {filled.map((e, i) => {
                        const pl = plans[i]; if (!pl) return null;
                        const exp = entryDate(e);
                        return (
                          <div key={e.uid} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '8px 12px', marginBottom: 8, background: 'var(--bg-app)' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 5 }}>
                              <i className="ti ti-file" style={{ marginRight: 5 }} aria-hidden />{e.type || '—'}{e.person ? ` — ${e.person}` : ''} · expiry {dmyEn(exp)}
                              {!pl.known && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, color: '#92400E', background: '#FEF3C7', borderRadius: 5, padding: '0 6px' }}>type not in the list — set reminders by hand</span>}
                            </div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, padding: '2px 0', cursor: 'pointer' }}>
                              <input type="checkbox" checked={pl.includeExpiry} onChange={() => setPlan(i, { includeExpiry: !pl.includeExpiry })} />
                              <b>Expiry</b> — {dmyEn(exp)}
                            </label>
                            {pl.reminders.map((rm, k) => (
                              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, padding: '2px 0', flexWrap: 'wrap' }}>
                                {/* std rows can't be unticked either — dropping the standard reminder
                                    is adjusting the standard (owner 2026-09-16). */}
                                <input type="checkbox" checked={rm.on} disabled={!!rm.std} onChange={() => setPlan(i, p2 => ({ ...p2, reminders: p2.reminders.map((x, y) => (y === k ? { ...x, on: !x.on } : x)) }))} />
                                <b>Reminder</b>
                                {rm.std ? (
                                  <b title="Standard lead time — fixed for this document type" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                    {rm.days}<i className="ti ti-lock" style={{ fontSize: 11, opacity: .55 }} aria-hidden />
                                  </b>
                                ) : (
                                  <input type="number" min={1} max={600} value={rm.days}
                                    onChange={ev => { const v = Math.max(1, Math.min(600, Number(ev.target.value) || 1)); setPlan(i, p2 => ({ ...p2, reminders: p2.reminders.map((x, y) => (y === k ? { ...x, days: v } : x)) })); }}
                                    style={{ ...inp, width: 74, padding: '4px 8px' }} />
                                )}
                                days before <span style={{ color: 'var(--text-tertiary)' }}>({dmyEn(remDate(exp, rm.days))})</span>
                                {rm.note && <span style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>— {rm.note}</span>}
                                {!rm.std && (
                                  <button type="button" onClick={() => setPlan(i, p2 => ({ ...p2, reminders: p2.reminders.filter((_, y) => y !== k) }))} title="Remove"
                                    style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-tertiary)', display: 'inline-flex' }}>
                                    <i className="ti ti-x" style={{ fontSize: 13 }} />
                                  </button>
                                )}
                              </div>
                            ))}
                            <button type="button" onClick={() => setPlan(i, p2 => ({ ...p2, reminders: [...p2.reminders, { on: true, days: 14, note: '' }] }))}
                              style={{ marginTop: 3, padding: '3px 10px', borderRadius: 7, border: '1px dashed var(--border-strong)', background: 'transparent', color: 'var(--accent)', fontWeight: 700, fontSize: 11.5, cursor: 'pointer' }}>
                              + Add reminder
                            </button>
                            {pl.dobOk && (
                              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, padding: '4px 0 0', cursor: 'pointer' }}>
                                <input type="checkbox" checked={pl.birthday} onChange={() => setPlan(i, { birthday: !pl.birthday })} />
                                <b>Birthday</b> — {dmyEn(e.ai?.dob || '')}, repeats yearly
                              </label>
                            )}
                          </div>
                        );
                      })}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
                        <button onClick={() => setConfirmStep(1)} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Back</button>
                        <button onClick={() => { if (!calTarget) return; setConfirming(false); setConfirmStep(1); void save(); }} disabled={!calTarget}
                          title={calTarget ? undefined : 'Choose which calendar the events go to'}
                          style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: calTarget ? 'var(--accent)' : 'var(--c-gray-100)', color: calTarget ? '#fff' : 'var(--text-tertiary)', fontWeight: 700, fontSize: 13, cursor: calTarget ? 'pointer' : 'default' }}>
                          <i className="ti ti-check" style={{ marginRight: 5 }} aria-hidden />Confirm &amp; save
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* A recorded test empties the wizard, and with no file there is no footer row, so an admin's tick
            box and Results link keep a row of their own, under the note on the test just recorded. */}
        {isAdmin && filled.length === 0 && (
          <>
            {simNote && (
              <div style={{ marginTop: 14, fontSize: 13, fontWeight: 700, color: '#15803D' }}>
                <i className="ti ti-circle-check" style={{ marginRight: 6 }} aria-hidden />{simNote}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: simNote ? 8 : 14 }}>{simControls}</div>
          </>
        )}
        {simResults && <SimResults onClose={() => setSimResults(false)} />}
      </div>
    </div>
  );
}
